(() => {
  if (customElements.get('product-shipping-estimator')) return;

  class ProductShippingEstimator extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-shipping-estimator-form]');
      this.input = this.querySelector('[name="cep"]');
      this.message = this.querySelector('[data-shipping-estimator-message]');
      this.results = this.querySelector('[data-shipping-estimator-results]');
      this.variantId = Number(this.dataset.variantId);

      this.input?.addEventListener('input', () => {
        this.input.value = this.formatZip(this.input.value);
      });

      this.form?.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      event.preventDefault();

      const zip = this.onlyDigits(this.input.value);
      if (zip.length !== 8) {
        this.setMessage('Informe um CEP válido com 8 dígitos.');
        this.clearResults();
        return;
      }

      const quantity = this.getCurrentQuantity();
      let cartBefore;

      this.setLoading(true);
      this.clearResults();

      try {
        const address = await this.getBrazilAddress(zip);
        cartBefore = await this.getCart();
        await this.addCurrentProduct(quantity);

        const rates = await this.getShippingRates(address);
        this.renderRates(rates, cartBefore.item_count > 0);
      } catch (error) {
        console.error(error);
        this.setMessage('Não foi possível calcular o frete agora. Tente novamente ou confira no checkout.');
      } finally {
        if (cartBefore) await this.restoreCart(cartBefore);
        this.setLoading(false);
      }
    }

    async getBrazilAddress(zip) {
      const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
      if (!response.ok) throw new Error('Zip lookup failed');

      const data = await response.json();
      if (data.erro || !data.uf) throw new Error('Zip not found');

      return {
        zip,
        province: this.getProvinceName(data.uf),
        city: data.localidade || '',
      };
    }

    getProvinceName(uf) {
      const provinces = {
        AC: 'Acre',
        AL: 'Alagoas',
        AP: 'Amapá',
        AM: 'Amazonas',
        BA: 'Bahia',
        CE: 'Ceará',
        DF: 'Distrito Federal',
        ES: 'Espírito Santo',
        GO: 'Goiás',
        MA: 'Maranhão',
        MT: 'Mato Grosso',
        MS: 'Mato Grosso do Sul',
        MG: 'Minas Gerais',
        PA: 'Pará',
        PB: 'Paraíba',
        PR: 'Paraná',
        PE: 'Pernambuco',
        PI: 'Piauí',
        RJ: 'Rio de Janeiro',
        RN: 'Rio Grande do Norte',
        RS: 'Rio Grande do Sul',
        RO: 'Rondônia',
        RR: 'Roraima',
        SC: 'Santa Catarina',
        SP: 'São Paulo',
        SE: 'Sergipe',
        TO: 'Tocantins',
      };

      return provinces[uf] || uf;
    }

    async getShippingRates(address) {
      const params = new URLSearchParams();
      params.set('shipping_address[zip]', address.zip);
      params.set('shipping_address[country]', 'Brazil');
      params.set('shipping_address[province]', address.province);
      if (address.city) params.set('shipping_address[city]', address.city);

      await fetch(`/cart/prepare_shipping_rates.json?${params.toString()}`, { method: 'POST' });

      for (let attempt = 0; attempt < 10; attempt += 1) {
        await this.wait(700);
        const response = await fetch(`/cart/async_shipping_rates.json?${params.toString()}`);
        if (!response.ok) continue;

        const data = await response.json();
        if (data.shipping_rates) return data.shipping_rates;
      }

      throw new Error('Shipping rates timeout');
    }

    async getCart() {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Cart unavailable');
      return response.json();
    }

    async addCurrentProduct(quantity) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: this.variantId,
          quantity,
        }),
      });

      if (!response.ok) throw new Error('Could not add product to cart simulation');
    }

    async restoreCart(cart) {
      await fetch('/cart/clear.js', { method: 'POST' });

      if (cart.items.length) {
        const items = cart.items.map((item) => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {},
          selling_plan: item.selling_plan_allocation?.selling_plan?.id,
        }));

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          note: cart.note || '',
          attributes: cart.attributes || {},
        }),
      });
    }

    renderRates(rates, hadCartItems) {
      if (!rates.length) {
        this.setMessage('Nenhuma opção de frete encontrada para este CEP.');
        return;
      }

      const notice = hadCartItems
        ? '<p class="product-shipping-estimator__notice">Estimativa considerando este produto e os itens já no carrinho.</p>'
        : '';

      this.message.textContent = '';
      this.results.innerHTML = `${notice}<ul class="product-shipping-estimator__list">
        ${rates
          .map(
            (rate) => `<li class="product-shipping-estimator__rate">
              <span class="product-shipping-estimator__rate-name">${rate.name}</span>
              <span class="product-shipping-estimator__rate-price">${this.formatMoney(rate.price)}</span>
            </li>`
          )
          .join('')}
      </ul>`;
    }

    getCurrentQuantity() {
      const quantityInput = document.querySelector('quantity-input .quantity__input');
      const quantity = Number(quantityInput?.value || 1);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    formatMoney(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return value;

      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(number);
    }

    formatZip(value) {
      const digits = this.onlyDigits(value).slice(0, 8);
      return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    }

    onlyDigits(value) {
      return String(value || '').replace(/\D/g, '');
    }

    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    setLoading(isLoading) {
      const button = this.querySelector('button');
      if (button) {
        button.disabled = isLoading;
        button.textContent = isLoading ? 'Calculando...' : 'Calcular';
      }

      if (isLoading) this.setMessage('Calculando opções de frete...');
    }

    setMessage(text) {
      this.message.textContent = text;
    }

    clearResults() {
      this.results.innerHTML = '';
    }
  }

  customElements.define('product-shipping-estimator', ProductShippingEstimator);
})();
